import { Link } from "@tanstack/react-router";
import { Skeleton } from "@/components/kit";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { StockMovementRowDTO } from "@/modules/reports/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  ReportMobileCard,
  ReportProductLink,
  ReportStat,
} from "./ReportProductLink";

const REASON_LABELS: Record<string, string> = {
  order_paid: "ຫຼຸດອໍເດີ້",
  order_cancelled: "ຍົກເລີກ",
  order_returned: "ຕີກັບ",
  manual_adjust: "ປັບມື",
  goods_receipt: "ຮັບເຂົ້າ lot",
  goods_receipt_void: "ຍົກເລີກຮັບເຂົ້າ",
  import_order_receive: "ຮັບນຳເຂົ້າ",
};

function formatStockDelta(row: StockMovementRowDTO): string {
  const absQty = Math.abs(row.quantityDelta);
  const formatted = formatQtyWithUnit(absQty, row.stockUnitLabel);
  if (row.quantityDelta > 0) return `+${formatted}`;
  if (row.quantityDelta < 0) return `−${formatted}`;
  return formatted;
}

function deltaColorClass(delta: number): string {
  if (delta > 0) return "text-leaf";
  if (delta < 0) return "text-destructive";
  return "";
}

function isGoodsReceiptReason(reason: string): boolean {
  return reason === "goods_receipt" || reason === "goods_receipt_void";
}

type StockMovementsTableProps = {
  rows?: StockMovementRowDTO[];
  isLoading?: boolean;
};

function StockMovementMobileCard({ row }: { row: StockMovementRowDTO }) {
  return (
    <ReportMobileCard>
      <div className="flex items-start justify-between gap-2">
        <ReportProductLink
          productId={row.variantId}
          productName={row.productName}
          variantName={row.variantName}
        />
        <span
          className={`shrink-0 font-medium text-sm tabular-nums ${deltaColorClass(row.quantityDelta)}`}
        >
          {formatStockDelta(row)}
        </span>
      </div>
      <dl className="mt-2.5 grid grid-cols-2 gap-2 text-sm">
        <ReportStat
          label="ເຫດການ"
          value={
            <>
              {REASON_LABELS[row.reason] ?? row.reason}
              {isGoodsReceiptReason(row.reason) && row.referenceId ? (
                <Link
                  to="/app/inventory/receive/$id"
                  params={{ id: row.referenceId }}
                  className="block text-secondary text-xs underline-offset-2 hover:underline"
                >
                  ເບິ່ງໃບຮັບເຂົ້າ
                </Link>
              ) : null}
            </>
          }
        />
        <ReportStat
          label="ເວລາ"
          value={formatDateTimeLocal(row.createdAt)}
          valueClassName="text-muted-foreground"
        />
      </dl>
    </ReportMobileCard>
  );
}

export function StockMovementsTable({
  rows = [],
  isLoading,
}: StockMovementsTableProps) {
  const header = (
    <div className="flex flex-col gap-3 border-secondary/15 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div>
        <h2 className="font-display text-lg">ປະຫວັດສະຕັອກ</h2>
        <p className="text-muted-foreground text-sm">
          ການເຄື່ອນໄຫວສະຕັອກ (ຫນ່ວຍຂາຍ) ຕາມເວລາ
        </p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="brand-card overflow-hidden">
        {header}
        <div className="flex flex-col gap-2 p-4">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <Skeleton key={k} className="h-10 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="brand-card overflow-hidden">
        {header}
        <p className="p-6 text-center text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
      </div>
    );
  }

  return (
    <div className="brand-card overflow-hidden">
      {header}

      {/* Mobile card list */}
      <div className="flex flex-col gap-2 p-3 md:hidden">
        {rows.map((row) => (
          <StockMovementMobileCard key={row.id} row={row} />
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-secondary/10 border-b text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium sm:px-5">ເວລາ</th>
              <th className="px-4 py-2 font-medium sm:px-5">ສິນຄ້າ</th>
              <th className="px-4 py-2 text-right font-medium sm:px-5">ປ່ຽນ</th>
              <th className="px-4 py-2 font-medium sm:px-5">ເຫດການ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-secondary/5 border-b last:border-0"
              >
                <td className="px-4 py-2.5 text-muted-foreground sm:px-5">
                  {formatDateTimeLocal(row.createdAt)}
                </td>
                <td className="px-4 py-2.5 sm:px-5">
                  <div className="font-medium">{row.productName}</div>
                  <div className="text-muted-foreground text-xs">
                    {row.variantName}
                  </div>
                </td>
                <td
                  className={`px-4 py-2.5 text-right font-medium tabular-nums sm:px-5 ${deltaColorClass(row.quantityDelta)}`}
                >
                  {formatStockDelta(row)}
                </td>
                <td className="px-4 py-2.5 sm:px-5">
                  <div>{REASON_LABELS[row.reason] ?? row.reason}</div>
                  {isGoodsReceiptReason(row.reason) && row.referenceId ? (
                    <Link
                      to="/app/inventory/receive/$id"
                      params={{ id: row.referenceId }}
                      className="text-secondary text-xs underline-offset-2 hover:underline"
                    >
                      ເບິ່ງໃບຮັບເຂົ້າ
                    </Link>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
